import { useCallback } from 'react';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import type { SettingsSectionId } from '~/lib/settings-sections';
import type { SystemSettingsSectionId } from '~/lib/system-settings-sections';
import { openSettingsWindow, openSystemSettingsWindow } from '~/features/windows/window-payloads';
import { useNavigate } from 'react-router';
import { settingsPath } from '~/lib/settings-sections';
import { systemSettingsPath } from '~/lib/system-settings-sections';
import { useUiRefactoring } from '~/lib/ui-refactoring';

/**
 * FRONT-1938: the one choke point every "Setup" trigger goes through —
 * mirrors `startNewChat`/`openExistingChat` in `app-header.tsx`, which route
 * every "open chat" trigger through `useChatOpenStore` for a similar reason:
 * a chat open-request QUEUED but not yet consumed has to be cleared, or it
 * reopens a stale chat well after Settings closed, with no visible connection
 * to this click.
 *
 * Settings is always a WINDOW (eddy, 2026-10-26: "all settings should be a
 * window that we can handle like the others") — docked beside the focused
 * window when there is one, on its own otherwise. The `/settings` route is
 * only a door into the same window now (`routes/settings.tsx`).
 */
export function useOpenSettings(): (section?: SettingsSectionId) => void {
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);
  const navigate = useNavigate();
  // FRONT-3223: with `eddy.ui-refactoring` off, Settings is the `/settings` route modal again.
  const windowsOn = useUiRefactoring();
  return useCallback(
    (section) => {
      clearOpenChat();
      if (windowsOn) openSettingsWindow(section);
      else navigate(section ? settingsPath(section) : '/settings');
    },
    [clearOpenChat, windowsOn, navigate],
  );
}

/** FRONT-2376: the System Settings counterpart to {@link useOpenSettings} — also always a window. */
export function useOpenSystemSettings(): (section?: SystemSettingsSectionId) => void {
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);
  const navigate = useNavigate();
  const windowsOn = useUiRefactoring();
  return useCallback(
    (section) => {
      clearOpenChat();
      if (windowsOn) openSystemSettingsWindow(section);
      else navigate(section ? systemSettingsPath(section) : '/system-settings');
    },
    [clearOpenChat, windowsOn, navigate],
  );
}
